import i18n from 'i18next';
import ICU from 'i18next-icu';
import ca from 'i18next-icu/locale-data/ca';
import en from 'i18next-icu/locale-data/en';
import es from 'i18next-icu/locale-data/es';
import fr from 'i18next-icu/locale-data/fr';
import ja from 'i18next-icu/locale-data/ja';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-chained-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import {
  getLocaleStringInternal,
  getLanguageCode,
} from 'typescript/utilities/locales';
import { Env, getEnvironment } from 'typescript/utilities/environment';
import { captureException } from '@sentry/react';

const env = getEnvironment();

const lazyLoadTranslation = async (locale: string) => {
  try {
    const { default: json } = await import(
      `config/translations/${locale}.json`
    );

    return json;
  } catch (error) {
    captureException(error);

    if (env === Env.DEVELOPMENT) {
      console.error(`Failed to load translation for locale: '${locale}'.`);
    }

    return {};
  }
};

i18n
  .use(initReactI18next)
  .use(Backend)
  .use(
    new ICU({
      localeData: [ca, en, es, fr, ja],
    }),
  )
  .init({
    lng: getLanguageCode(),
    fallbackLng: 'en',
    ns: [getLocaleStringInternal(), 'en'],
    defaultNS: getLocaleStringInternal(),
    fallbackNS: 'en',
    debug: ![Env.PRODUCTION, Env.TEST].includes(env),
    interpolation: {
      escapeValue: false,
      format: (value, format) => {
        if (format === 'uppercase') return value.toUpperCase();
        if (format === 'lowercase') return value.toLowerCase();
        if (format === 'capitalize') {
          return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
        }
        return value;
      },
    },
    backend: {
      backends: [
        resourcesToBackend((_language, locale, callback) => {
          lazyLoadTranslation(locale).then((data) => callback(null, data));
        }),
      ],
    },
    react: {
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 's'],
      useSuspense: true,
    },
  });

export { default } from 'i18next';
